import type { ReactNode } from 'react';
import { Check, CircleDot, Minus } from 'lucide-react';
import Logo from '../common/Logo';
import { useAuth } from '../../contexts/AuthContext';

export default function AuthFrame({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  const { branding } = useAuth();
  const appName = branding?.app_name ?? 'Heymakr';

  return (
    <div className="auth-site grid min-h-screen lg:grid-cols-[minmax(20rem,0.8fr)_minmax(32rem,1.2fr)]">
      <aside className="relative hidden min-h-screen overflow-hidden bg-app-chrome text-white lg:flex lg:flex-col lg:justify-between lg:p-10 xl:p-14">
        <div className="absolute inset-y-0 right-0 w-px bg-white/20" aria-hidden="true" />
        <div className="relative flex items-center gap-3">
          <Logo className="h-10 w-10 object-contain" src={branding?.logo_url} alt={appName} />
          <span className="text-lg font-semibold">{appName}</span>
        </div>

        <div className="relative max-w-lg py-12">
          <p className="font-mono text-xs font-semibold uppercase text-app-accent">Prepared before. Precise during.</p>
          <h2 className="mt-5 font-display text-4xl font-semibold leading-tight xl:text-5xl">Every conversation begins with stronger evidence.</h2>
          <p className="mt-5 max-w-md text-sm leading-7 text-app-chrome-muted">Account intelligence prepares the opportunity. Mavis helps the representative close material qualification gaps while the buyer is present.</p>
        </div>

        <div className="relative border border-white/15 bg-white/[0.04] p-5">
          <div className="mb-5 flex items-center justify-between gap-4">
            <p className="font-mono text-[11px] font-semibold uppercase text-app-chrome-muted">Opportunity evidence</p>
            <span className="bg-app-accent px-2 py-1 font-mono text-[10px] font-semibold uppercase text-app-accent-text">Live</span>
          </div>
          <div className="space-y-3">
            {[
              ['Business impact', 'Established', Check, 'text-app-positive'],
              ['Decision criteria', 'Partial', CircleDot, 'text-app-warning'],
              ['Approval path', 'Missing', Minus, 'text-app-critical'],
            ].map(([label, state, Icon, tone]) => (
              <div key={label as string} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-white/15 pb-3 last:border-0 last:pb-0">
                <Icon className={`h-4 w-4 ${tone as string}`} aria-hidden="true" />
                <span className="text-sm font-medium">{label as string}</span>
                <span className="font-mono text-[10px] uppercase text-app-chrome-muted">{state as string}</span>
              </div>
            ))}
          </div>
        </div>
      </aside>

      <main className="flex min-h-screen items-center justify-center px-5 py-10 sm:px-8 lg:px-12">
        <div className="w-full max-w-md">
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <Logo className="h-10 w-10 object-contain" src={branding?.logo_url} alt={appName} />
            <span className="text-lg font-semibold text-app-ink">{appName}</span>
          </div>
          <p className="app-kicker mb-3">{eyebrow}</p>
          <h1 className="font-display text-3xl font-semibold leading-tight text-app-ink sm:text-4xl">{title}</h1>
          <p className="mt-3 text-sm leading-6 text-app-muted">{description}</p>
          <div className="mt-8">{children}</div>
        </div>
      </main>
    </div>
  );
}
